import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import desertoursLogo from '../assets/desertours-logo.png';
import { useAuth } from '../hooks/useAuth';
import { validateEmail, validatePassword } from '../lib/auth/validation';
import { getAuthErrorMessage } from '../lib/auth/errors';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const { user, loading, signInWithPassword } = useAuth();

  // Si l'utilisateur est déjà connecté, rediriger vers la home
  if (!loading && user) {
    return <Navigate to="/" replace />;
  }

  // Si on est en train de charger l'état d'auth, afficher un loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900"></div>
      </div>
    );
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation côté client avec les fonctions centralisées
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);

    if (emailError) {
      setMessage(emailError);
      return;
    }

    if (passwordError) {
      setMessage(passwordError);
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const { error } = await signInWithPassword(email.trim(), password);

      if (error) {
        setMessage(getAuthErrorMessage(error));
      } else {
        setMessage('✅ Connexion réussie !');
      }
    } catch (err) {
      console.error('Erreur lors de la connexion:', err);
      setMessage("Une erreur inattendue s'est produite. Veuillez réessayer plus tard");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white min-h-screen flex items-center justify-center">
      <div className="flex flex-col gap-6 items-center justify-start max-w-[24rem] w-[24rem]">
        {/* Logo */}
        <img src={desertoursLogo} alt="Desertours Logo" className="shrink-0 size-12 object-cover" />

        {/* Header */}
        <div className="flex flex-col gap-3 items-start justify-start text-center w-full">
          <div className="font-bold text-[1.875rem] text-zinc-950 w-full leading-[2.25rem]">
            CRM Desertours
          </div>
          <div className="font-normal text-[0.875rem] text-zinc-500 w-full leading-[1.25rem]">
            Connectez-vous pour accéder à votre espace Admin
          </div>
        </div>

        {/* Form */}
        <div className="flex flex-col gap-6 items-center justify-start rounded-[0.5rem] w-full">
          <form
            onSubmit={handleLogin}
            className="flex flex-col gap-2 items-start justify-start w-full"
          >
            {/* Email Input */}
            <div className="flex flex-col gap-2 items-start justify-start w-full">
              <div className="bg-white h-9 relative rounded-[0.375rem] w-full">
                <Input
                  type="email"
                  placeholder="E-mail"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  className="h-9 px-3 py-1 border border-zinc-200 rounded-[0.375rem] shadow-[0px_0.0625rem_0.125rem_0px_rgba(0,0,0,0.05)] text-[0.875rem] placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                />
              </div>
            </div>

            {/* Password Input */}
            <div className="flex flex-col gap-2 items-start justify-start w-full">
              <div className="bg-white h-9 relative rounded-[0.375rem] w-full">
                <Input
                  type="password"
                  placeholder="Mot de passe"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  className="h-9 px-3 py-1 border border-zinc-200 rounded-[0.375rem] shadow-[0px_0.0625rem_0.125rem_0px_rgba(0,0,0,0.05)] text-[0.875rem] placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                />
              </div>
            </div>
          </form>

          {/* Button and Forgot Password */}
          <div className="flex flex-col gap-6 items-center justify-start w-full">
            <Button
              type="submit"
              onClick={handleLogin}
              className="bg-zinc-900 hover:bg-zinc-800 text-neutral-50 h-9 px-4 py-2 rounded-[0.375rem] shadow-[0px_0.0625rem_0.1875rem_0px_rgba(0,0,0,0.1),0px_0.0625rem_0.125rem_0px_rgba(0,0,0,0.06)] w-full font-medium text-[0.875rem] leading-[1.25rem]"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Connexion en cours...
                </>
              ) : (
                'Se connecter'
              )}
            </Button>

            {/* Forgot Password Link */}
            <div className="flex flex-col gap-6 items-center justify-start w-full">
              <Link
                to="/forgot-password"
                className="font-normal text-[0.875rem] text-zinc-500 underline hover:text-zinc-700 transition-colors"
              >
                Mot de passe oublié ?
              </Link>
            </div>
          </div>

          {/* Message de feedback */}
          {message && (
            <div
              className={`text-sm p-3 rounded-md w-full ${
                message.includes('✅')
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}
            >
              {message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
